import React from "react";
import { FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { Box, HStack } from "@chakra-ui/layout";
import * as Yup from "yup";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { DecimalField } from "../components/fields/DecimalField/DecimalField";
import { FormStack } from "../components/FormStack/FormStack";

export interface GeoLocationDetailFormValues {
  displayName?: string;
  relevantText?: string;
  latitude?: string;
  longitude?: string;
  altitude?: string;
  address?: string;
}

interface GeoLocationDetailFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  geoLocation?: CherryHubAdminApi.GeoLocation | null;
  onCancel: () => void;
  onSuccess: (geoLocation: CherryHubAdminApi.GeoLocation) => void;
  onFailure: (message?: string) => void;
}

const emptyStrToUndefined = (value?: string): string | undefined => {
  if (value == null || value.trim() === "") return undefined;
  return value;
};

const InnerForm = ({
  isSubmitting,
  submitForm,
  onCancel,
}: GeoLocationDetailFormProps & FormikProps<GeoLocationDetailFormValues>) => {
  return (
    <Box width="100%" maxW="800px" alignSelf="flex-start">
      <FormStack>
        <TextField horizontal label="Display Name" name="displayName" />
        <TextField
          horizontal
          label="Default Location Message"
          name="relevantText"
        />
        <DecimalField
          horizontal
          label="Latitude"
          name="latitude"
          decimalPlaces={5}
          min={-90}
          max={90}
        />
        <DecimalField
          horizontal
          label="Longitude"
          name="longitude"
          decimalPlaces={5}
          min={-180}
          max={180}
        />
        <DecimalField
          horizontal
          label="Altitude"
          name="altitude"
          decimalPlaces={2}
        />
        <TextField horizontal label="Address" name="address" />

        <HStack width="100%" justifyContent="center" spacing="3" pt="8">
          <Button
            colorScheme="cherryButton"
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={submitForm}
          >
            Save
          </Button>
          <Button disabled={isSubmitting} onClick={onCancel}>
            Cancel
          </Button>
        </HStack>
      </FormStack>
    </Box>
  );
};

export const GeoLocationDetailForm = withFormik<
  GeoLocationDetailFormProps,
  GeoLocationDetailFormValues
>({
  mapPropsToValues: ({ geoLocation }) => ({
    displayName: geoLocation?.DisplayName ?? "",
    relevantText: geoLocation?.RelevantText ?? "",
    latitude:
      geoLocation?.Latitude != null ? geoLocation.Latitude.toString() : "",
    longitude:
      geoLocation?.Longitude != null ? geoLocation.Longitude.toString() : "",
    altitude:
      geoLocation?.Altitude != null ? geoLocation.Altitude.toString() : "",
    address: geoLocation?.Address ?? "",
  }),

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: false,

  validationSchema: Yup.object().shape({
    displayName: Yup.string().trim().required("Display Name is required."),
    relevantText: Yup.string()
      .trim()
      .required("Default Location Message is required."),
    latitude: Yup.number()
      .typeError("Latitude must be a number.")
      .required("Latitude is required.")
      .min(-90, "Latitude must be between -90 and 90.")
      .max(90, "Latitude must be between -90 and 90."),
    longitude: Yup.number()
      .typeError("Longitude must be a number.")
      .required("Longitude is required.")
      .min(-180, "Longitude must be between -180 and 180.")
      .max(180, "Longitude must be between -180 and 180."),
    altitude: Yup.number()
      .typeError("Altitude must be a number.")
      .nullable()
      .notRequired(),
  }),

  handleSubmit: async (values, { props }) => {
    const update: CherryHubAdminApi.GeoLocationUpdate = {
      DisplayName: values.displayName!.trim(),
      RelevantText: values.relevantText!.trim(),
      Latitude: Number(values.latitude),
      Longitude: Number(values.longitude),
      Altitude:
        emptyStrToUndefined(values.altitude) != null
          ? Number(values.altitude)
          : undefined,
      Address: emptyStrToUndefined(values.address),
    };

    const result = props.geoLocation?.Meta?.Id
      ? await props.apiClient.updateGeoLocation(
          props.businessId,
          props.geoLocation.Meta.Id,
          update
        )
      : await props.apiClient.createGeoLocation(props.businessId, update);

    if (result.ok) {
      props.onSuccess(result.data);
    } else {
      props.onFailure(result.message);
    }
  },
})(InnerForm);
